import React, { useState, useEffect } from "react"
import { getFromStorage, setToStorage } from "../data/utility/asyncStorageUtils"
import { View, StyleSheet } from "react-native"
import { apiRequest } from "@data/redux/actions/api"
import { ScreenSkeleton } from "../common/screens/ScreenSkeleton"
import { connect } from "react-redux"
import { fastTrackService } from "../data/services/fastTrackService"
import TitleM from "../common/components/text/TitleM"
import TextM from "../common/components/text/TextM"
import { useForm } from "react-hook-form"
import FilledButtonM from "../common/components/button/FilledButtonM"
import { theme } from "../common/theme/theme"
import TextButtonM from "../common/components/button/TextButtonM"
import { useGetFastTrackUserOnMount } from "./hooks/useGetFastTrackUserOnMount"
import { useApiStatus } from "../common/hooks/useApiStatus"
import { useNavigation } from "@react-navigation/native"
import { DigitsCodeInputM } from "../common/components/input/DigitsCodeInputM"
import { useCountDown } from "../common/hooks/useCountDown"
import { useHasFinishedFastTrack } from "./hooks/useHasFinishedFastTrack"

const LAST_SEND_TIME_KEY = "@verification_code_last_send_time"
const setLastSendTime = async (date) => setToStorage(LAST_SEND_TIME_KEY, date)
const getLastSendTime = async () => getFromStorage(LAST_SEND_TIME_KEY)

export const FastTrackTrialConfirmationScreen = (props) => {
    const navigation = useNavigation()
    const isLoading = props.isSendingCode || props.isVerifyingCode
    const verificationMethodLabels = {
        email: "email",
        sms: "SMS",
    }

    const { control, setValue } = useForm()
    const [verificationMethod, setVerificationMethod] = useState("sms")
    const { countdown, initializeCountdown } = useCountDown({
        getLastSendTime,
        setLastSendTime,
        durationInSec: 60,
    })

    const { initialLoading, email, emailB64, hasFinishedFastTrack } =
        useGetFastTrackUserOnMount(props)

    useEffect(() => {
        initializeCountdown()
    }, [email, initialLoading])

    const getOtherVerificationMethod = () => {
        return verificationMethod === "email" ? "sms" : "email"
    }

    const getVerificationMethodLabel = (opposite = false) => {
        const method = opposite
            ? getOtherVerificationMethod()
            : verificationMethod
        return verificationMethodLabels[method]
    }

    const sendCode = async (verification_method = verificationMethod) => {
        props.apiRequest(
            fastTrackService.sendVerificationCode,
            {},
            { verification_method, email }
        )
        await initializeCountdown()
    }

    const changeVerificationMethod = async () => {
        const newVerificationMethod = getOtherVerificationMethod()
        setVerificationMethod(newVerificationMethod)
        await sendCode(newVerificationMethod)
    }

    const verifyCode = () => {
        const code = Object.entries(control._formValues)
            .map(([, value]) => value)
            .join("")

        props.apiRequest(
            fastTrackService.verifyVerificationCode,
            {},
            { code, email }
        )
    }
    useApiStatus(fastTrackService.verifyVerificationCode, null, true, () => {
        navigation.navigate("register/fast-track/thank-you", { user: emailB64 })
    })

    useHasFinishedFastTrack(hasFinishedFastTrack, emailB64)

    return (
        <ScreenSkeleton withCenterContent isLoading={initialLoading}>
            <View style={styles.container}>
                <View style={styles.stack}>
                    <TitleM fontWeight="ExtraBold">Confirmez votre RDV</TitleM>
                    <TextM wrap>
                        Pour confirmer le rendez-vous, entrez le code reçu par{" "}
                        {getVerificationMethodLabel()}.
                    </TextM>
                </View>
                <View style={styles.card}>
                    <TitleM fontWeight="ExtraBold">Entrez le code reçu</TitleM>
                    <DigitsCodeInputM
                        length={6}
                        control={control}
                        setValue={setValue}
                    />
                    <View style={[styles.stack, styles.center]}>
                        <FilledButtonM
                            style={styles.button}
                            onPress={verifyCode}
                            loading={isLoading}
                            label="Valider"
                        />
                        <View style={styles.row}>
                            <TextM wrap>
                                Vous n&apos;avez pas reçu de code ?
                            </TextM>
                            <TextButtonM
                                fontWeight="ExtraBold"
                                onPress={sendCode}
                                loading={isLoading}
                                disabled={countdown > 0}
                                label={
                                    "Renvoyer le code par " +
                                    getVerificationMethodLabel()
                                }
                            />
                        </View>
                        {countdown > 0 && (
                            <View style={styles.timerContainer}>
                                <TextM
                                    color={theme.colors.primary}
                                    fontWeight="Medium"
                                    wrap
                                >
                                    Prochain renvoi possible dans {countdown}{" "}
                                    secondes
                                </TextM>
                            </View>
                        )}
                        <TextButtonM
                            fontWeight="Bold"
                            label={`Recevoir un code par ${getVerificationMethodLabel(
                                true
                            )}`}
                            loading={isLoading}
                            disabled={countdown > 0}
                            onPress={() => changeVerificationMethod()}
                        />
                    </View>
                </View>
            </View>
        </ScreenSkeleton>
    )
}

const styles = StyleSheet.create({
    container: {
        paddingVertical: 40,
        gap: 40,
    },
    stack: {
        gap: 10,
    },
    center: {
        alignItems: "center",
    },
    card: {
        padding: 25,
        paddingHorizontal: 10,
        alignItems: "center",
        gap: 50,
        borderWidth: 1,
        borderColor: theme.colors.light,
        backgroundColor: theme.colors.white,
        borderRadius: 16,
        width: "100%",
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        justifyContent: "center",
    },
    button: {
        width: "100%",
        flex: 1,
        maxWidth: 400,
    },
    timerContainer: {
        alignItems: "center",
        paddingVertical: 10,
    },
})

const mapStateToProps = (state) => {
    return {
        isSendingCode: state.api.sendVerificationCode?.request,
        isVerifyingCode: state.api.verifyVerificationCode?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FastTrackTrialConfirmationScreen)
